import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Button,
   TextField,
   Box,
   Typography,
   IconButton,
   Alert,
   Chip,
   Divider,
   List,
   ListItem,
   ListItemText,
   ListItemSecondaryAction,
   ListItemAvatar,
   Avatar,
   CircularProgress,
   Accordion,
   AccordionSummary,
   AccordionDetails,
   Autocomplete,
   FormControl,
   InputLabel,
   MenuItem,
   Select,
   Badge,
   Tooltip,
   FormControlLabel,
   Switch,
   Paper,
} from '@mui/material';
import {
   Close as CloseIcon,
   PersonAdd as PersonAddIcon,
   Email as EmailIcon,
   Group as GroupIcon,
   Remove as RemoveIcon,
   ExpandMore as ExpandMoreIcon,
   AdminPanelSettings as AdminIcon,
   AdminPanelSettings as CoOwnerIcon,
   Person as PersonIcon,
   ContactMail as ContactMailIcon,
   CheckCircle as AcceptedIcon,
   Schedule as PendingIcon,
   Cancel as DeclinedIcon,
   AccessTime as TimeIcon,
   Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';
import { getAuthHeaders } from '../../utils/auth';

interface AlbumMember {
   id: string;
   email: string;
   displayName?: string;
   photoURL?: string;
   role: 'owner' | 'editor' | 'viewer';
   joinedAt?: string;
}

interface Contact {
   name: string;
   email: string;
}

interface InvitationWithDetails {
   id: string;
   albumId: string;
   email: string;
   inviterName: string;
   inviterId: string;
   status: 'pending' | 'accepted' | 'declined';
   role: string;
   personalMessage?: string;
   recipientName?: string;
   createdAt: any;
   acceptedAt?: any;
   declinedAt?: any;
   userDetails?: {
      uid: string;
      displayName: string;
      email: string;
      photoURL?: string;
   };
}

interface AlbumInviteDialogProps {
   open: boolean;
   onClose: () => void;
   album: {
      id: string;
      title: string;
      description?: string;
      isPublic?: boolean;
      memberIds?: string[];
      invitedEmails?: string[];
      createdBy?: string;
      members?: Array<{
         userId: string;
         email: string;
         displayName?: string;
         photoURL?: string;
         role: 'owner' | 'editor' | 'viewer';
         joinedAt?: any;
      }>;
   } | null;
   onInviteSuccess?: () => void;
   onAlbumUpdate?: (updatedAlbum: any) => void; // Add callback for album updates
   currentUserId?: string; // Add current user ID to check permissions
}

interface AlbumInviteDialogRef {
   refreshData: () => Promise<void>;
   forceRefreshWithAlbumData: (albumData: any) => Promise<void>;
}

const AlbumInviteDialog = forwardRef<AlbumInviteDialogRef, AlbumInviteDialogProps>(
   ({ open, onClose, album, onInviteSuccess, onAlbumUpdate, currentUserId }, ref) => {
      const { currentUser } = useUser();
      const [email, setEmail] = useState('');
      const [recipientName, setRecipientName] = useState('');
      const [message, setMessage] = useState('');
      const [loading, setLoading] = useState(false);
      const [error, setError] = useState('');
      const [success, setSuccess] = useState('');

      // Member management states
      const [members, setMembers] = useState<AlbumMember[]>([]);
      const [loadingMembers, setLoadingMembers] = useState(false);

      // Contact selection states
      const [contacts, setContacts] = useState<Contact[]>([]);
      const [loadingContacts, setLoadingContacts] = useState(false);
      const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
      const [useExistingContact, setUseExistingContact] = useState(false);

      // Invitation history states
      const [invitationHistory, setInvitationHistory] = useState<InvitationWithDetails[]>([]);
      const [loadingInvitations, setLoadingInvitations] = useState(false);
      const [deletingInvite, setDeletingInvite] = useState<string | null>(null);
      const [removingMember, setRemovingMember] = useState<string | null>(null);
      const [resendingInvite, setResendingInvite] = useState<string | null>(null);

      // Add refresh state
      const [refreshing, setRefreshing] = useState(false);

      // Role selection state
      const [selectedRole, setSelectedRole] = useState<'viewer' | 'editor' | 'owner'>('viewer');

      // Check if current user has permission to send invites (owner or editor only)
      const canSendInvites = React.useMemo(() => {
         if (!album || !currentUser) {
            console.log('🔍 AlbumInvite: No album or currentUser', {
               album: !!album,
               currentUser: !!currentUser,
            });
            return false;
         }

         console.log('🔍 AlbumInvite: Checking permissions for:', {
            albumId: album.id,
            albumTitle: album.title,
            currentUserId: currentUser.uid,
            currentUserEmail: currentUser.email,
            albumMembers: album.members,
         });

         // Check user role in members array (role-based access control only)
         if (album.members && Array.isArray(album.members)) {
            const userMember = album.members.find(
               (member) => member.userId === currentUser.uid || member.email === currentUser.email
            );
            console.log('🔍 AlbumInvite: Found user member:', userMember);
            const hasPermission =
               userMember && (userMember.role === 'owner' || userMember.role === 'editor');
            console.log('🔍 AlbumInvite: Has permission:', hasPermission);
            return hasPermission;
         }

         console.log('❌ AlbumInvite: No members array or user not found');
         return false;
      }, [album, currentUser]);

      // Debug button state
      React.useEffect(() => {
         console.log('🔍 AlbumInvite: Button state debug:', {
            canSendInvites,
            email: email.trim(),
            recipientName: recipientName.trim(),
            useExistingContact,
            selectedContact,
            loading,
            buttonShouldBeEnabled:
               canSendInvites &&
               ((!useExistingContact && email.trim()) || (useExistingContact && selectedContact)) &&
               recipientName.trim() &&
               !loading,
         });
      }, [canSendInvites, email, recipientName, useExistingContact, selectedContact, loading]);

      useEffect(() => {
         if (open && album) {
            // Clear existing data to avoid showing stale information
            setMembers([]);
            setInvitationHistory([]);
            setError('');
            setSuccess('');

            // Load fresh data
            refreshAllData();
         }
      }, [open, album]);

      // Expose refresh methods to parent components
      useImperativeHandle(
         ref,
         () => ({
            refreshData: async () => {
               console.log('Manual refresh triggered by parent component');
               await refreshAllData();
            },
            forceRefreshWithAlbumData: async (albumData: any) => {
               console.log('Force refresh with album data triggered by parent component');
               await refreshAllData(albumData);
            },
         }),
         []
      );

      // Manual refresh function for user-triggered refresh
      const handleManualRefresh = async () => {
         setRefreshing(true);
         setError('');
         setSuccess('');

         try {
            console.log('Manual refresh triggered by user...');
            const freshAlbumData = await fetchFreshAlbumData();
            await refreshAllData(freshAlbumData);

            if (onAlbumUpdate && freshAlbumData) {
               onAlbumUpdate(freshAlbumData);
            }

            setSuccess('Album data refreshed successfully');
            setTimeout(() => setSuccess(''), 3000);
         } catch (error) {
            console.error('Manual refresh failed:', error);
            setError('Failed to refresh data. Please try again.');
            setTimeout(() => setError(''), 5000);
         } finally {
            setRefreshing(false);
         }
      };

      const refreshAllData = async (useAlbumData?: any) => {
         const albumToUse = useAlbumData || album;
         if (!albumToUse) return;

         console.log('Refreshing all dialog data for album:', albumToUse.id);

         try {
            // Load invitation history first since pending invites are derived from it
            await fetchInvitationHistory();

            // Load other data in parallel
            await Promise.all([loadAlbumMembers(albumToUse), fetchContacts()]);

            // Note: loadPendingInvites is no longer needed since pending invites are
            // automatically updated by the useEffect when invitation history changes
            console.log('All dialog data refreshed successfully');

            // Development helper: Check for data inconsistencies
            if (process.env.NODE_ENV === 'development') {
               setTimeout(() => validateDataConsistency(), 1000);
            }
         } catch (error) {
            console.error('Error refreshing dialog data:', error);
         }
      };

      // Development helper function to validate data consistency
      const validateDataConsistency = () => {
         if (!album || process.env.NODE_ENV !== 'development') return;

         interface DataInconsistency {
            type: 'member_without_invitation' | 'accepted_invitation_without_member';
            email: string;
            memberId?: string;
            invitationId?: string;
         }

         const inconsistencies: DataInconsistency[] = [];

         // Check for members without accepted invitation records
         members.forEach((member) => {
            const hasAcceptedInvitation = invitationHistory.some(
               (inv) =>
                  inv.email.toLowerCase() === member.email.toLowerCase() &&
                  inv.status === 'accepted'
            );

            if (
               !hasAcceptedInvitation &&
               member.email.toLowerCase() !== album.createdBy?.toLowerCase()
            ) {
               inconsistencies.push({
                  type: 'member_without_invitation',
                  email: member.email,
                  memberId: member.id,
               });
            }
         });

         // Check for accepted invitations without corresponding members
         invitationHistory.forEach((invitation) => {
            if (invitation.status === 'accepted') {
               const isMember = members.some(
                  (member) => member.email.toLowerCase() === invitation.email.toLowerCase()
               );

               if (!isMember) {
                  inconsistencies.push({
                     type: 'accepted_invitation_without_member',
                     email: invitation.email,
                     invitationId: invitation.id,
                  });
               }
            }
         });

         if (inconsistencies.length > 0) {
            console.warn('🚨 Data inconsistencies detected in album:', album.id, inconsistencies);
         } else {
            console.log('✅ Data consistency check passed for album:', album.id);
         }
      };

      const fetchInvitationHistory = async () => {
         if (!album) return;

         setLoadingInvitations(true);
         try {
            const { getAuthHeaders } = await import('../../utils/auth');
            const headers = await getAuthHeaders();

            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/album/${album.id}/invitations`,
               {
                  headers,
               }
            );

            if (response.ok) {
               const data = await response.json();
               console.log('Invitation history fetched:', data); // Add debug log

               // Debug log to see timestamp formats and user details
               if (data.invitations && data.invitations.length > 0) {
                  console.log('Sample invitation data:', {
                     email: data.invitations[0].email,
                     userDetails: data.invitations[0].userDetails,
                     createdAt: data.invitations[0].createdAt,
                     acceptedAt: data.invitations[0].acceptedAt,
                     declinedAt: data.invitations[0].declinedAt,
                  });
               }

               setInvitationHistory(data.invitations || []);
               console.log('✅ Invitation history loaded successfully:', {
                  count: (data.invitations || []).length,
                  statuses: (data.invitations || []).map(
                     (inv: InvitationWithDetails) => inv.status
                  ),
                  emails: (data.invitations || []).map((inv: InvitationWithDetails) => inv.email),
               });
            } else {
               console.error(
                  'Failed to fetch invitation history:',
                  response.status,
                  response.statusText
               );
               // Set empty array on error to ensure the useEffect still triggers
               setInvitationHistory([]);
            }
         } catch (err) {
            console.error('Error fetching invitation history:', err);
            // Set empty array on error to ensure the useEffect still triggers
            setInvitationHistory([]);
         } finally {
            setLoadingInvitations(false);
         }
      };

      const fetchContacts = async () => {
         if (!currentUser) return;

         setLoadingContacts(true);
         try {
            const { getAuthHeaders } = await import('../../utils/auth');
            const headers = await getAuthHeaders();

            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/people/contacts?userId=${currentUser.uid}`,
               {
                  headers,
               }
            );

            if (response.ok) {
               const data = await response.json();
               console.log('Contacts fetched from relationship_network:', data);

               // Transform the relationship_network data to match Contact interface
               const contactsList = (data.data || []).map((contact: any) => ({
                  name: contact.displayName || contact.email,
                  email: contact.email,
               }));

               setContacts(contactsList);

               // If no contacts available and user was trying to use existing contacts, switch to email input
               if (contactsList.length === 0 && useExistingContact) {
                  setUseExistingContact(false);
                  setSelectedContact(null);
               }
            } else {
               console.error('Failed to fetch contacts:', response.status, response.statusText);
            }
         } catch (err) {
            console.error('Error fetching contacts:', err);
            // Don't show error to user, just fail silently for contacts
         } finally {
            setLoadingContacts(false);
         }
      };

      const loadAlbumMembers = async (freshAlbumData?: any) => {
         // Use fresh album data if provided, otherwise use the album from props
         const albumToUse = freshAlbumData || album;
         if (!albumToUse) return;

         const memberIds = albumToUse.memberIds || [];
         console.log(
            'Loading album members for album:',
            albumToUse.id,
            'memberIds:',
            memberIds,
            'memberIds type:',
            typeof memberIds,
            'is array:',
            Array.isArray(memberIds)
         );

         // If no member IDs, set empty members and return
         if (!Array.isArray(memberIds) || memberIds.length === 0) {
            console.log('No valid member IDs found, setting empty members array');
            setMembers([]);
            return;
         }

         setLoadingMembers(true);
         try {
            const { getAuthHeaders } = await import('../../utils/auth');
            const headers = await getAuthHeaders();

            const requestBody = { userIds: memberIds };
            console.log('Sending request to /api/albums/users/batch with body:', requestBody);

            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/albums/users/batch`,
               {
                  method: 'POST',
                  headers,
                  body: JSON.stringify(requestBody),
               }
            );

            console.log('Load members API response:', {
               status: response.status,
               statusText: response.statusText,
               ok: response.ok,
            });

            if (response.ok) {
               const data = await response.json();
               console.log('Load members API data:', data);

               const membersWithRoles = data.users.map((user: any) => ({
                  ...user,
                  role: user.id === albumToUse.createdBy ? 'owner' : 'editor',
               }));

               console.log('Setting members to:', membersWithRoles);
               setMembers(membersWithRoles);
            } else {
               const errorData = await response.json().catch(() => ({}));
               console.error('Failed to load album members:', {
                  status: response.status,
                  statusText: response.statusText,
                  errorData,
               });
            }
         } catch (err) {
            console.error('Error loading album members:', err);
         } finally {
            setLoadingMembers(false);
         }
      };

      const fetchFreshAlbumData = async () => {
         if (!album) return null;

         try {
            const { getAuthHeaders } = await import('../../utils/auth');
            const headers = await getAuthHeaders();

            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/albums/${album.id}`,
               {
                  headers,
               }
            );

            if (response.ok) {
               const freshAlbum = await response.json();
               console.log('Fresh album data:', freshAlbum);
               return freshAlbum.album || freshAlbum;
            } else {
               console.error(
                  'Failed to fetch fresh album data:',
                  response.status,
                  response.statusText
               );
               return null;
            }
         } catch (err) {
            console.error('Error fetching fresh album data:', err);
            return null;
         }
      };

      const loadPendingInvites = async (freshAlbumData?: any) => {
         // This function is no longer needed since pending invites are shown in invitation history
         const albumToUse = freshAlbumData || album;
         if (!albumToUse) return;

         console.log('LoadPendingInvites called for album:', albumToUse.id);
         console.log('Note: Pending invites are now shown in invitation history');
      };

      const handleInvite = async () => {
         // Determine which email to use
         const rawEmail = useExistingContact ? selectedContact?.email : email.trim();

         if (!rawEmail || typeof rawEmail !== 'string' || !album || !currentUser) {
            setError('Please provide a valid email address');
            return;
         }

         if (!recipientName.trim()) {
            setError('Please provide a recipient name');
            return;
         }

         const emailToInvite = rawEmail.toLowerCase();

         // Check for existing invitations with different statuses
         const existingInvitation = invitationHistory.find(
            (inv) => inv.email && inv.email.toLowerCase() === emailToInvite
         );

         // Check if there's already a pending invitation
         if (existingInvitation?.status === 'pending') {
            setError('This person has already been invited to this album');
            return;
         }

         // Enhanced membership validation: Only block if they're a member AND have an accepted invitation
         const isAlreadyMember = members.some(
            (member) => member.email && member.email.toLowerCase() === emailToInvite
         );

         if (isAlreadyMember && existingInvitation?.status === 'accepted') {
            setError('This person is already a member of this album');
            return;
         }

         // Handle re-inviting scenarios
         if (existingInvitation) {
            if (existingInvitation.status === 'declined') {
               console.log('Re-inviting user who previously declined:', emailToInvite);
               // Allow the invitation to proceed
            } else if (existingInvitation.status === 'accepted' && isAlreadyMember) {
               setError('This person has already accepted an invitation to this album');
               return;
            }
            // If there's an accepted invitation but they're not in members list,
            // or if there's a cancelled invitation, allow re-inviting
         }

         // Additional check: if they're a member but we're allowing re-invite
         // (e.g., no accepted invitation record), log it for debugging
         if (isAlreadyMember && (!existingInvitation || existingInvitation.status !== 'accepted')) {
            console.log('Allowing invitation for member without accepted invitation record:', {
               email: emailToInvite,
               albumId: album.id,
               isAlreadyMember,
               existingInvitationStatus: existingInvitation?.status || 'none',
               reason: 'This could be due to direct membership addition or cancelled invitation',
            });
         }

         setLoading(true);
         setError('');
         setSuccess('');

         try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/album/invite`, {
               method: 'POST',
               headers,
               body: JSON.stringify({
                  albumId: album.id,
                  email: emailToInvite,
                  role: selectedRole,
                  customMessage: message.trim(),
                  recipientName: recipientName.trim(),
               }),
            });

            if (!response.ok) {
               const errorData = await response.json();

               // Handle specific error cases with user-friendly messages
               if (errorData.code === 'DUPLICATE_INVITATION') {
                  throw new Error('This person has already been invited to this album');
               } else if (errorData.code === 'ALREADY_ACCEPTED') {
                  throw new Error('This person has already accepted an invitation for this album');
               } else if (errorData.code === 'ALREADY_MEMBER') {
                  throw new Error('This person is already a member of this album');
               } else if (errorData.code === 'ALREADY_MEMBER_DATA_INCONSISTENCY') {
                  // Handle the specific data inconsistency case with a more helpful error
                  setError(
                     errorData.error + ' Please try refreshing the page to update the member list.'
                  );
                  // Automatically refresh the data after showing the error
                  setTimeout(async () => {
                     console.log('Auto-refreshing data due to data inconsistency...');
                     try {
                        const freshAlbumData = await fetchFreshAlbumData();
                        await refreshAllData(freshAlbumData);
                        if (onAlbumUpdate && freshAlbumData) {
                           onAlbumUpdate(freshAlbumData);
                        }
                        setError(''); // Clear error after successful refresh
                        setSuccess('Member list has been updated. You can now try inviting again.');
                        setTimeout(() => setSuccess(''), 5000);
                     } catch (refreshError) {
                        console.error('Failed to auto-refresh data:', refreshError);
                        setError(
                           'Unable to refresh data automatically. Please refresh the page manually.'
                        );
                     }
                  }, 3000);
                  return; // Early return to avoid the generic error handling below
               } else {
                  throw new Error(errorData.error || 'Failed to send invitation');
               }
            }

            const invitedEmail = emailToInvite;
            setSuccess(`Invitation sent to ${invitedEmail}`);

            // Clear form immediately for next invitation
            setEmail('');
            setRecipientName('');
            setMessage('');
            setSelectedContact(null);
            setUseExistingContact(false);

            // Clear success message after 3 seconds to allow multiple invitations
            setTimeout(() => {
               setSuccess('');
            }, 3000);

            // Refresh all related data after successful invitation
            console.log('Refreshing all data after successful invitation...');
            const freshAlbumData = await fetchFreshAlbumData();
            await refreshAllData(freshAlbumData);

            // Trigger parent callback if provided
            if (onInviteSuccess) {
               onInviteSuccess();
            }

            // Update parent with fresh album data if callback provided
            if (onAlbumUpdate && freshAlbumData) {
               onAlbumUpdate(freshAlbumData);
            }
         } catch (err) {
            console.error('Error sending invitation:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to send invitation';
            setError(errorMessage);

            // Clear error after 8 seconds to give user time to read it
            setTimeout(() => {
               setError('');
            }, 8000);
         } finally {
            setLoading(false);
         }
      };

      const handleRemoveMember = async (memberId: string) => {
         if (!album || memberId === album.createdBy) return;

         // Clear any existing messages
         setError('');
         setSuccess('');
         setRemovingMember(memberId);

         // Find the member that will be removed for debugging
         const memberToRemove = members.find((member) => member.id === memberId);
         console.log('Attempting to remove member:', {
            memberId,
            memberEmail: memberToRemove?.email,
            memberName: memberToRemove?.displayName,
            albumId: album.id,
            albumTitle: album.title,
         });

         try {
            const { getAuthHeaders } = await import('../../utils/auth');
            const headers = await getAuthHeaders();

            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/albums/${album.id}/remove-member`,
               {
                  method: 'POST',
                  headers,
                  body: JSON.stringify({
                     userId: memberId,
                  }),
               }
            );

            console.log('Remove member API response:', {
               status: response.status,
               statusText: response.statusText,
               ok: response.ok,
            });

            if (response.ok) {
               const result = await response.json();
               console.log('Remove member API result:', result);

               const memberName =
                  memberToRemove?.displayName || memberToRemove?.email?.split('@')[0] || 'Member';

               // Fetch fresh album data to get updated memberIds
               console.log('Fetching fresh album data after member removal...');
               const freshAlbumData = await fetchFreshAlbumData();

               // Update parent component with fresh album data
               if (freshAlbumData && onAlbumUpdate) {
                  console.log('Updating parent component with fresh album data...');
                  onAlbumUpdate(freshAlbumData);
               }

               // Refresh all related data with fresh album data
               console.log('Refreshing all album data after member removal...');
               await refreshAllData(freshAlbumData);
               console.log('All album data refreshed successfully');

               // Show success message with details about what was cleaned up
               let successMessage = `${memberName} removed from album`;
               if (result.deletedInvitations && result.deletedInvitations > 0) {
                  successMessage += ` and ${result.deletedInvitations} invitation record${result.deletedInvitations > 1 ? 's' : ''} deleted`;
               }
               setSuccess(successMessage);

               // Clear success message after 4 seconds (slightly longer for longer message)
               setTimeout(() => {
                  setSuccess('');
               }, 4000);

               // Notify parent component to refresh album data
               if (onInviteSuccess) {
                  console.log('Notifying parent component to refresh album data');
                  onInviteSuccess();
               }
            } else {
               const errorData = await response.json().catch(() => ({}));
               console.error('Remove member API error:', errorData);
               throw new Error(errorData.message || errorData.error || 'Failed to remove member');
            }
         } catch (err) {
            console.error('Error in handleRemoveMember:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to remove member';
            setError(errorMessage);

            // Clear error after 5 seconds
            setTimeout(() => {
               setError('');
            }, 5000);
         } finally {
            setRemovingMember(null);
         }
      };

      const handleCancelInvite = async (email: string) => {
         if (!album || !album.id) {
            console.error('Missing album or album.id:', { album });
            setError('Album information is missing');
            return;
         }

         if (!email) {
            console.error('Missing email for invitation cancellation');
            setError('Email is required to cancel invitation');
            return;
         }

         console.log('Cancelling invitation:', { albumId: album.id, email });
         setDeletingInvite(email);

         try {
            const { getAuthHeaders } = await import('../../utils/auth');
            const headers = await getAuthHeaders();

            const url = `${process.env.REACT_APP_API_URL}/api/albums/${album.id}/cancel-invite`;
            console.log('Cancel invite URL:', url);

            const response = await fetch(url, {
               method: 'POST',
               headers,
               body: JSON.stringify({
                  email,
               }),
            });

            if (response.ok) {
               const result = await response.json();
               console.log('Invitation record deleted:', result);

               // Fetch fresh album data to ensure member list is up to date
               console.log('Fetching fresh album data after invitation deletion...');
               const freshAlbumData = await fetchFreshAlbumData();

               // Update parent component with fresh album data if available
               if (freshAlbumData && onAlbumUpdate) {
                  console.log('Updating parent component with fresh album data after deletion...');
                  onAlbumUpdate(freshAlbumData);
               }

               // Refresh all related data with fresh album data
               await refreshAllData(freshAlbumData);

               setSuccess(`Invitation record deleted for ${email}`);

               // Clear success message after 3 seconds
               setTimeout(() => {
                  setSuccess('');
               }, 3000);

               // Trigger parent callback to refresh album data
               if (onInviteSuccess) {
                  onInviteSuccess();
               }
            } else {
               const errorData = await response.json().catch(() => ({}));
               console.error('Cancel invite failed:', {
                  status: response.status,
                  statusText: response.statusText,
                  errorData,
               });
               throw new Error(
                  errorData.error || errorData.message || 'Failed to delete invitation record'
               );
            }
         } catch (err: any) {
            console.error('Error deleting invitation record:', err);
            setError(err.message || 'Failed to delete invitation record');

            // Clear error after 5 seconds
            setTimeout(() => {
               setError('');
            }, 5000);
         } finally {
            setDeletingInvite(null);
         }
      };

      const handleResendInvite = async (invitationId: string) => {
         if (!album || !album.id) {
            console.error('Missing album or album.id:', { album });
            setError('Album information is missing');
            return;
         }

         if (!invitationId) {
            console.error('Missing invitation ID for resend');
            setError('Invitation ID is required to resend invitation');
            return;
         }

         console.log('Resending invitation:', { albumId: album.id, invitationId });
         setResendingInvite(invitationId);

         try {
            const { getAuthHeaders } = await import('../../utils/auth');
            const headers = await getAuthHeaders();

            const url = `${process.env.REACT_APP_API_URL}/api/album/resend-invite`;
            console.log('Resend invite URL:', url);

            const response = await fetch(url, {
               method: 'POST',
               headers,
               body: JSON.stringify({
                  invitationId,
               }),
            });

            if (response.ok) {
               const result = await response.json();
               console.log('Invitation resent successfully:', result);

               setSuccess('Invitation resent successfully!');

               // Refresh invitation history
               await fetchInvitationHistory();

               // Clear success message after 5 seconds
               setTimeout(() => {
                  setSuccess('');
               }, 5000);
            } else {
               const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
               console.error('Failed to resend invitation:', {
                  status: response.status,
                  statusText: response.statusText,
                  errorData,
               });
               throw new Error(
                  errorData.error || errorData.message || 'Failed to resend invitation'
               );
            }
         } catch (err: any) {
            console.error('Error resending invitation:', err);
            setError(err.message || 'Failed to resend invitation');

            // Clear error after 5 seconds
            setTimeout(() => {
               setError('');
            }, 5000);
         } finally {
            setResendingInvite(null);
         }
      };

      const handleClose = () => {
         // Clear all form state and messages
         setEmail('');
         setRecipientName('');
         setMessage('');
         setError('');
         setSuccess('');
         setLoading(false); // Ensure loading state is cleared
         setSelectedContact(null);
         setUseExistingContact(false);

         // Clear all loading states
         setRemovingMember(null);
         setDeletingInvite(null);

         onClose();
      };

      // Use the canSendInvites logic for managing members (owner and editor can manage)
      const canManageMembers = canSendInvites;

      const getStatusColor = (status: string) => {
         switch (status) {
            case 'accepted':
               return 'success';
            case 'pending':
               return 'warning';
            case 'declined':
               return 'error';
            default:
               return 'default';
         }
      };

      const getStatusIcon = (status: string) => {
         switch (status) {
            case 'accepted':
               return <AcceptedIcon color='success' />;
            case 'pending':
               return <PendingIcon color='warning' />;
            case 'declined':
               return <DeclinedIcon color='error' />;
            default:
               return <TimeIcon />;
         }
      };

      const formatDate = (timestamp: any) => {
         if (!timestamp) return 'Unknown';

         console.log('formatDate called with:', timestamp, 'Type:', typeof timestamp);

         try {
            let date: Date;

            // Handle Firebase Timestamp objects (with .toDate() method)
            if (timestamp.toDate && typeof timestamp.toDate === 'function') {
               date = timestamp.toDate();
            }
            // Handle Firebase server timestamp format: { seconds: number, nanoseconds: number }
            else if (timestamp.seconds && typeof timestamp.seconds === 'number') {
               date = new Date(timestamp.seconds * 1000);
            }
            // Handle ISO date strings
            else if (typeof timestamp === 'string') {
               date = new Date(timestamp);
            }
            // Handle numbers (milliseconds since epoch)
            else if (typeof timestamp === 'number') {
               date = new Date(timestamp);
            }
            // Handle Date objects
            else if (timestamp instanceof Date) {
               date = timestamp;
            }
            // Handle other object formats that might have a _seconds property
            else if (timestamp._seconds && typeof timestamp._seconds === 'number') {
               date = new Date(timestamp._seconds * 1000);
            } else {
               // Try to create a date from whatever we have
               date = new Date(timestamp);
            }

            // Validate the date
            if (isNaN(date.getTime())) {
               console.warn('Invalid date created from timestamp:', timestamp);
               return 'Unknown';
            }

            return date.toLocaleDateString('en-US', {
               year: 'numeric',
               month: 'short',
               day: 'numeric',
            });
         } catch (error) {
            console.error('Error formatting date:', error, 'Timestamp:', timestamp);
            return 'Unknown';
         }
      };

      if (!album) return null;

      return (
         <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
            <DialogTitle>
               <Box display='flex' alignItems='center' justifyContent='space-between'>
                  <Box display='flex' alignItems='center' gap={1}>
                     <GroupIcon color='primary' />
                     <Typography variant='h6'>Manage Album Access</Typography>
                  </Box>
                  <Tooltip title='Refresh album data and member list'>
                     <span>
                        <IconButton onClick={handleManualRefresh} disabled={refreshing}>
                           {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
                        </IconButton>
                     </span>
                  </Tooltip>
                  <IconButton onClick={handleClose}>
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>

            <DialogContent>
               <Box mb={2}>
                  <Typography variant='h6' gutterBottom>
                     {album.title}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                     {album.isPublic ? 'Public Album' : 'Private Album'} • {members.length} member
                     {members.length !== 1 ? 's' : ''}
                  </Typography>
               </Box>

               {error && (
                  <Alert
                     severity='error'
                     sx={{ mb: 2 }}
                     action={
                        error.includes('data inconsistency') || error.includes('refresh') ? (
                           <Button
                              color='inherit'
                              size='small'
                              onClick={handleManualRefresh}
                              disabled={refreshing}
                           >
                              {refreshing ? 'Refreshing...' : 'Refresh Now'}
                           </Button>
                        ) : null
                     }
                  >
                     {error}
                  </Alert>
               )}

               {success && (
                  <Alert severity='success' sx={{ mb: 2 }}>
                     {success}
                  </Alert>
               )}

               {/* Permission check message */}
               {!canSendInvites && (
                  <Alert severity='info' sx={{ mb: 2 }}>
                     Only album owners and editors can send invitations to new members.
                  </Alert>
               )}

               {/* Invite New Members Section */}
               {canManageMembers ? (
                  <Accordion defaultExpanded>
                     <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant='subtitle1'>
                           <PersonAddIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                           Invite New Members
                        </Typography>
                     </AccordionSummary>
                     <AccordionDetails>
                        <Box
                           component='form'
                           onSubmit={(e) => {
                              e.preventDefault();
                              handleInvite();
                           }}
                        >
                           {/* Contact Selection Toggle */}
                           <Box sx={{ mb: 2 }}>
                              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                                 Choose invitation method:
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                 <Button
                                    variant={!useExistingContact ? 'contained' : 'outlined'}
                                    size='small'
                                    onClick={() => setUseExistingContact(false)}
                                    startIcon={<EmailIcon />}
                                 >
                                    New Email
                                 </Button>
                                 <Button
                                    variant={useExistingContact ? 'contained' : 'outlined'}
                                    size='small'
                                    onClick={() => {
                                       if (loadingContacts) {
                                          return; // Don't allow clicks while loading
                                       }

                                       if (contacts.length > 0) {
                                          setUseExistingContact(true);
                                       } else {
                                          // Show a brief message that no contacts are available
                                          setError(
                                             'No previous contacts found. Please enter a new email address.'
                                          );
                                          setTimeout(() => setError(''), 3000);
                                          setUseExistingContact(false);
                                       }
                                    }}
                                    disabled={contacts.length === 0 && !loadingContacts}
                                 >
                                    Contacts
                                 </Button>
                              </Box>
                              {useExistingContact && contacts.length === 0 && !loadingContacts && (
                                 <Typography
                                    variant='caption'
                                    color='text.secondary'
                                    sx={{ mt: 1, display: 'block' }}
                                 >
                                    No previous contacts found. Use the "New Email" option instead.
                                 </Typography>
                              )}
                           </Box>

                           {/* Contact Selection or Email Input */}
                           {useExistingContact && contacts.length > 0 ? (
                              <Autocomplete
                                 options={contacts}
                                 value={selectedContact}
                                 onChange={(event, newValue) => {
                                    setSelectedContact(newValue || null);
                                    // Clear messages when user selects contact
                                    if (success) setSuccess('');
                                    if (error) setError('');
                                 }}
                                 getOptionLabel={(option) => option.name}
                                 renderOption={(props, option) => (
                                    <Box component='li' {...props}>
                                       <Box>
                                          <Typography variant='body2' sx={{ fontWeight: 500 }}>
                                             {option.name}
                                          </Typography>
                                          <Typography variant='caption' color='text.secondary'>
                                             {option.email}
                                          </Typography>
                                       </Box>
                                    </Box>
                                 )}
                                 renderInput={(params) => (
                                    <TextField
                                       {...params}
                                       label='Select Previous Contact'
                                       margin='normal'
                                       required
                                       InputProps={{
                                          ...params.InputProps,
                                          startAdornment: (
                                             <ContactMailIcon
                                                sx={{ mr: 1, color: 'text.secondary' }}
                                             />
                                          ),
                                       }}
                                    />
                                 )}
                                 loading={loadingContacts}
                                 loadingText='Loading contacts...'
                                 noOptionsText={
                                    loadingContacts ? 'Loading contacts...' : 'No contacts found'
                                 }
                                 fullWidth
                              />
                           ) : (
                              <TextField
                                 label='Email Address'
                                 type='email'
                                 fullWidth
                                 value={email}
                                 onChange={(e) => {
                                    setEmail(e.target.value);
                                    // Clear messages when user starts typing new email
                                    if (success) setSuccess('');
                                    if (error) setError('');
                                 }}
                                 required
                                 margin='normal'
                                 InputProps={{
                                    startAdornment: (
                                       <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                    ),
                                 }}
                              />
                           )}

                           <TextField
                              label='Recipient Name'
                              type='text'
                              fullWidth
                              required
                              value={recipientName}
                              onChange={(e) => {
                                 setRecipientName(e.target.value);
                                 // Clear messages when user starts typing recipient name
                                 if (success) setSuccess('');
                                 if (error) setError('');
                              }}
                              margin='normal'
                              placeholder="Enter the recipient's name for display"
                              InputProps={{
                                 startAdornment: (
                                    <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                 ),
                              }}
                              helperText='This name will be shown in the invitation history'
                              error={!recipientName.trim() && recipientName !== ''}
                           />

                           <TextField
                              label='Personal Message (Optional)'
                              multiline
                              rows={3}
                              fullWidth
                              value={message}
                              onChange={(e) => setMessage(e.target.value)}
                              margin='normal'
                              placeholder='Add a personal message to your invitation...'
                           />

                           {/* Role selection */}
                           <FormControl fullWidth sx={{ mt: 2 }}>
                              <InputLabel>Role</InputLabel>
                              <Select
                                 value={selectedRole}
                                 label='Role'
                                 onChange={(e) =>
                                    setSelectedRole(e.target.value as 'viewer' | 'editor' | 'owner')
                                 }
                              >
                                 <MenuItem value='viewer'>
                                    <Box>
                                       <Typography variant='body2'>Viewer</Typography>
                                       <Typography variant='caption' color='text.secondary'>
                                          Can view album content only
                                       </Typography>
                                    </Box>
                                 </MenuItem>
                                 <MenuItem value='editor'>
                                    <Box>
                                       <Typography variant='body2'>Editor</Typography>
                                       <Typography variant='caption' color='text.secondary'>
                                          Can add/remove photos and invite others
                                       </Typography>
                                    </Box>
                                 </MenuItem>
                                 <MenuItem value='owner'>
                                    <Box>
                                       <Typography variant='body2'>Owner</Typography>
                                       <Typography variant='caption' color='text.secondary'>
                                          Full control over album and members
                                       </Typography>
                                    </Box>
                                 </MenuItem>
                              </Select>
                           </FormControl>

                           <Button
                              type='submit'
                              variant='contained'
                              fullWidth
                              disabled={
                                 (!useExistingContact && !email.trim()) ||
                                 (useExistingContact && !selectedContact) ||
                                 !recipientName.trim() ||
                                 loading ||
                                 !canSendInvites
                              }
                              startIcon={
                                 loading ? <CircularProgress size={16} /> : <PersonAddIcon />
                              }
                              sx={{ mt: 2 }}
                           >
                              Send Invitation
                           </Button>
                        </Box>
                     </AccordionDetails>
                  </Accordion>
               ) : (
                  <Alert severity='info' sx={{ mb: 2 }}>
                     <Typography>Only album owners can send invitations.</Typography>
                  </Alert>
               )}

               <Divider sx={{ my: 2 }} />

               {/* Current Members Section */}
               <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                     <Typography variant='subtitle1'>Current Members ({members.length})</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                     {loadingMembers ? (
                        <Box display='flex' justifyContent='center' p={2}>
                           <CircularProgress size={24} />
                        </Box>
                     ) : (
                        <List>
                           {members.map((member) => (
                              <ListItem key={member.id}>
                                 <Avatar src={member.photoURL} sx={{ mr: 2 }}>
                                    {member.displayName?.[0] || member.email?.[0] || 'U'}
                                 </Avatar>
                                 <ListItemText
                                    primary={
                                       <Box display='flex' alignItems='center' gap={1}>
                                          <Typography variant='body1'>
                                             {member.displayName ||
                                                member.email?.split('@')[0] ||
                                                'Unknown'}
                                          </Typography>
                                          {member.role === 'owner' && (
                                             <Chip
                                                icon={<AdminIcon />}
                                                label='Owner'
                                                size='small'
                                                color='primary'
                                             />
                                          )}
                                       </Box>
                                    }
                                    secondary={member.email}
                                 />
                                 {canManageMembers && member.role !== 'owner' && (
                                    <ListItemSecondaryAction>
                                       <IconButton
                                          edge='end'
                                          onClick={() => handleRemoveMember(member.id)}
                                          color='error'
                                          size='small'
                                          disabled={removingMember === member.id}
                                       >
                                          {removingMember === member.id ? (
                                             <CircularProgress size={20} />
                                          ) : (
                                             <RemoveIcon />
                                          )}
                                       </IconButton>
                                    </ListItemSecondaryAction>
                                 )}
                              </ListItem>
                           ))}
                        </List>
                     )}
                  </AccordionDetails>
               </Accordion>

               {/* Invitation History Section */}
               <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                     <Typography variant='subtitle1'>
                        Invitation History ({invitationHistory.length})
                     </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                     {loadingInvitations ? (
                        <Box display='flex' justifyContent='center' p={2}>
                           <CircularProgress size={24} />
                        </Box>
                     ) : invitationHistory.length === 0 ? (
                        <Typography variant='body2' color='text.secondary'>
                           No invitation history found.
                        </Typography>
                     ) : (
                        <List>
                           {invitationHistory.map((invitation) => (
                              <ListItem key={invitation.id}>
                                 <ListItemAvatar>
                                    <Avatar
                                       src={invitation.userDetails?.photoURL || undefined}
                                       alt={invitation.userDetails?.displayName || invitation.email}
                                    >
                                       {!invitation.userDetails?.photoURL &&
                                          (
                                             invitation.recipientName ||
                                             invitation.userDetails?.displayName ||
                                             (invitation.email
                                                ? invitation.email.split('@')[0]
                                                : null) ||
                                             'U'
                                          )
                                             .charAt(0)
                                             .toUpperCase()}
                                    </Avatar>
                                 </ListItemAvatar>
                                 <ListItemText
                                    primary={
                                       <Box display='flex' alignItems='center' gap={1}>
                                          <Typography variant='body1'>
                                             {invitation.recipientName ||
                                                invitation.userDetails?.displayName ||
                                                (invitation.email
                                                   ? invitation.email.split('@')[0]
                                                   : null) ||
                                                'Unknown User'}
                                          </Typography>
                                          <Chip
                                             icon={getStatusIcon(invitation.status)}
                                             label={
                                                (invitation.status || 'pending')
                                                   .charAt(0)
                                                   .toUpperCase() +
                                                (invitation.status || 'pending').slice(1)
                                             }
                                             size='small'
                                             color={getStatusColor(invitation.status) as any}
                                             variant='outlined'
                                          />
                                       </Box>
                                    }
                                    secondary={
                                       <Box>
                                          {invitation.email && (
                                             <>
                                                <Typography
                                                   variant='caption'
                                                   color='text.secondary'
                                                >
                                                   {invitation.email}
                                                </Typography>
                                                <br />
                                             </>
                                          )}
                                          <Typography variant='caption' color='text.secondary'>
                                             Invited: {formatDate(invitation.createdAt)}
                                             {invitation.acceptedAt && (
                                                <>
                                                   {' '}
                                                   • Accepted: {formatDate(invitation.acceptedAt)}
                                                </>
                                             )}
                                             {invitation.declinedAt && (
                                                <>
                                                   {' '}
                                                   • Declined: {formatDate(invitation.declinedAt)}
                                                </>
                                             )}
                                          </Typography>
                                       </Box>
                                    }
                                 />
                                 {canManageMembers && (
                                    <ListItemSecondaryAction>
                                       <Box display='flex' gap={1}>
                                          {/* Resend button - only show for pending invitations */}
                                          {invitation.status === 'pending' && (
                                             <Tooltip title='Resend Invitation'>
                                                <span>
                                                   <IconButton
                                                      onClick={() => {
                                                         if (invitation.id) {
                                                            handleResendInvite(invitation.id);
                                                         } else {
                                                            console.error(
                                                               'No invitation ID found for resend:',
                                                               invitation
                                                            );
                                                            setError(
                                                               'Cannot resend invitation: invitation ID not found'
                                                            );
                                                         }
                                                      }}
                                                      size='small'
                                                      disabled={
                                                         resendingInvite === invitation.id ||
                                                         !invitation.id
                                                      }
                                                   >
                                                      {resendingInvite === invitation.id ? (
                                                         <CircularProgress size={20} />
                                                      ) : (
                                                         <RefreshIcon />
                                                      )}
                                                   </IconButton>
                                                </span>
                                             </Tooltip>
                                          )}

                                          {/* Cancel/Delete button */}
                                          <Tooltip
                                             title={
                                                invitation.status === 'pending'
                                                   ? 'Cancel Invitation'
                                                   : 'Delete Invitation Record'
                                             }
                                          >
                                             <span>
                                                <IconButton
                                                   edge='end'
                                                   onClick={() => {
                                                      if (invitation.email) {
                                                         handleCancelInvite(invitation.email);
                                                      } else {
                                                         console.error(
                                                            'No email found for invitation:',
                                                            invitation
                                                         );
                                                         setError(
                                                            'Cannot cancel invitation: email not found'
                                                         );
                                                      }
                                                   }}
                                                   size='small'
                                                   disabled={
                                                      deletingInvite === invitation.email ||
                                                      !invitation.email
                                                   }
                                                >
                                                   {deletingInvite === invitation.email ? (
                                                      <CircularProgress size={20} />
                                                   ) : (
                                                      <RemoveIcon />
                                                   )}
                                                </IconButton>
                                             </span>
                                          </Tooltip>
                                       </Box>
                                    </ListItemSecondaryAction>
                                 )}
                              </ListItem>
                           ))}
                        </List>
                     )}
                  </AccordionDetails>
               </Accordion>
            </DialogContent>

            <DialogActions>
               <Button onClick={handleClose}>Close</Button>
            </DialogActions>
         </Dialog>
      );
   }
);

AlbumInviteDialog.displayName = 'AlbumInviteDialog';

export default AlbumInviteDialog;
